<script setup>
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import AutoSaveCounter from './AutoSaveCounter.vue'
import { useDragAndDropStore } from './DragAndDropStore.js'


defineProps(['prioritise', 'allocate', 'shard', 'count'])

const store = useDragAndDropStore()
const { round, sharding, highlights, extra, lastSaved } = storeToRefs(store)

const titleCase = (title) => {
  return title.charAt(0).toUpperCase() + title.substr(1)
}

const isElimination = computed(() => round.value?.stage === 'E')

const currentHighlightKey = computed(() => {
  const currentKey = Object.keys(highlights.value).filter(key => highlights.value[key].active)
  if (currentKey.length > 0) {
    return currentKey[0]
  }
  return false
})

const shardingEnabled = computed(() => sharding.value.index !== null)

</script>

<template>
  <nav class="navbar navbar-default navbar-light fixed-top px-2">
    <div
      class="btn-toolbar"
      role="toolbar"
      aria-label="Toolbar with button groups"
    >
      <div class="btn-group btn-group-sm">
        <a
          :href="extra.backUrl"
          class="btn btn-outline-primary"
          data-toggle="tooltip"
          data-placement="bottom"
          :title="extra.backLabel"
        >
          <i data-feather="chevron-left" />
        </a>
        <auto-save-counter :last-saved="lastSaved" />
        <slot name="extra-actions" />
        <button
          v-if="prioritise"
          :class="['btn btn-outline-primary', count > 0 ? '' : 'disabled btn-no-hover']"
          @click="$emit('show-prioritise')"
        >
          {{ gettext('Prioritise') }}
        </button>
        <button
          v-if="allocate"
          :class="['btn btn-outline-primary', count > 0 ? '' : 'disabled btn-no-hover']"
          @click="$emit('show-allocate')"
        >
          {{ gettext('Allocate') }}
        </button>
        <button
          v-if="shard"
          :class="['btn ', count > 0 ? '' : 'disabled btn-no-hover',
                   shardingEnabled ? 'btn-primary' : 'btn-outline-primary']"
          @click="$emit('show-shard')"
        >
          <i data-feather="server" />
        </button>
      </div>
      <div class="dropdown">
        <button
          id="dropdownMenuOffset"
          class="btn btn-sm ml-2 btn-outline-primary dropdown-toggle"
          data-toggle="dropdown"
          title="Sort the draw"
        >
          <i data-feather="list" />
        </button>
        <div
          class="dropdown-menu"
          aria-labelledby="dropdownMenuOffset"
        >
          <a
            class="dropdown-item"
            href="#"
            @click="store.setSorting('room_rank')"
          >
            <span v-if="isElimination">Sort by Break Rank</span>
            <span v-if="!isElimination">Sort by Room Rank</span>
          </a>
          <a
            v-if="!isElimination"
            class="dropdown-item"
            href="#"
            @click="store.setSorting('bracket')"
          >
            Sort by Bracket
          </a>
          <a
            class="dropdown-item"
            href="#"
            @click="store.setSorting('importance')"
          >
            Sort by Importance
          </a>
          <a
            v-if="!isElimination"
            class="dropdown-item"
            href="#"
            @click="store.setSorting('liveness')"
          >
            Sort by Liveness
          </a>
        </div>
      </div>
    </div>

    <div class="btn-group btn-group-sm">
      <button
        class="btn btn-outline-secondary disabled d-xl-inline d-none"
        data-toggle="tooltip"
        :title="('Key for the color highlights.')"
      >
        <i data-feather="help-circle" />
      </button>
      <template v-if="!currentHighlightKey">
        <slot name="default-highlights" />
        <button
          class="btn btn-dark"
          data-toggle="tooltip"
          :title="('Has not been marked as available for this round or has been allocated twice.')"
        >
          {{ gettext('Unavailable') }}
        </button>
      </template>
      <template v-else>
        <button
          v-for="option in highlights[currentHighlightKey].options"
          :class="['btn btn-primary border-0', currentHighlightKey + '-display', option.css]"
        >
          {{ option.fields.name }}
        </button>
      </template>
    </div>

    <div class="btn-group btn-group-sm">
      <button
        v-for="(highlight, highlightKey) in highlights"
        :class="['btn btn-outline-primary', highlight.active ? 'btn-primary active' : '']"
        @click="store.toggleHighlight(highlightKey)"
      >
        <span><i :data-feather="highlight.active ? 'eye-off' : 'eye'" /></span>
        <span class="pl-1">{{ gettext(titleCase(highlightKey)) }}</span>
      </button>
    </div>
  </nav>
</template>
